import { formatMoneyObject } from 'shared/lib/formatMoneyObject'

export type RaffleDisplayStatus = 'upcoming' | 'active' | 'closed'

export const getRaffleDisplayStatus = (raffle: { startDate: string; endDate: string }): RaffleDisplayStatus => {
  const now = Date.now()
  const start = new Date(raffle.startDate).getTime()
  const end = new Date(raffle.endDate).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return 'closed'
  if (now < start) return 'upcoming'
  if (now > end) return 'closed'
  return 'active'
}

export const isRaffleVisible = (raffle: { startDate: string; endDate: string }): boolean => {
  const status = getRaffleDisplayStatus(raffle)
  return status === 'active' || status === 'upcoming'
}

export type DisplayTimeZone = 'ET' | 'JST'

const TIMEZONE_MAP: Record<DisplayTimeZone, string> = {
  ET: 'America/New_York',
  JST: 'Asia/Tokyo',
}

const getLocaleLang = (locale?: string) => {
  if (locale?.startsWith('ja')) return 'ja'
  return 'en'
}

const getIanaTimeZone = (timezone: DisplayTimeZone = 'ET') =>
  TIMEZONE_MAP[timezone] || TIMEZONE_MAP.ET

export const formatRaffleDate = (
  dateStr: string,
  timezone: DisplayTimeZone = 'ET',
  locale?: string,
) => {
  const lang = getLocaleLang(locale)
  const date = new Date(dateStr)
  const options: Intl.DateTimeFormatOptions = { timeZone: getIanaTimeZone(timezone) }

  if (lang === 'ja') {
    return new Intl.DateTimeFormat(lang, {
      ...options,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date)
  }

  const parts = new Intl.DateTimeFormat(lang, {
    ...options,
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  }).formatToParts(date)

  const month = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value
  const year = parts.find((p) => p.type === 'year')?.value
  return `${month}/${day}/${year}`
}

export const formatRaffleDateTime = (
  dateStr: string,
  timezone: DisplayTimeZone = 'ET',
  locale?: string,
) => {
  const lang = getLocaleLang(locale)
  const date = new Date(dateStr)
  const iana = getIanaTimeZone(timezone)

  if (lang === 'ja') {
    const formatted = new Intl.DateTimeFormat(lang, {
      timeZone: iana,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    }).format(date)
    return `${formatted} ${timezone}`
  }

  const datePart = formatRaffleDate(dateStr, timezone, locale)
  const timeParts = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date)

  const hour = timeParts.find((p) => p.type === 'hour')?.value || ''
  const minute = timeParts.find((p) => p.type === 'minute')?.value || '00'
  const dayPeriod = (timeParts.find((p) => p.type === 'dayPeriod')?.value || '').toLowerCase()
  const timePart = minute === '00' ? `${hour}${dayPeriod}` : `${hour}:${minute}${dayPeriod}`

  return `${datePart} at ${timePart} ${timezone}`
}

export const formatStoreList = (stores: string[]): string => {
  if (stores.length === 0) return ''
  if (stores.length === 1) return stores[0]
  return `${stores.slice(0, -1).join(', ')} and ${stores[stores.length - 1]}`
}

export const formatRafflePrice = (price: number, currency: string): string => {
  const symbol = currency === 'USD' ? '$' : currency === 'JPY' ? '¥' : currency
  return `${symbol}${price.toLocaleString('en-US')}`
}

export const formatRafflePriceForMeta = (price: number, currency: string): string =>
  formatMoneyObject(
    { amount: price * 100, amountUsdCents: price * 100, currency },
    { showCents: false, useGrouping: false },
  )

export const formatRaffleDateLong = (
  dateStr: string,
  timezone: DisplayTimeZone = 'ET',
  locale?: string,
): string => {
  const lang = getLocaleLang(locale)

  if (lang === 'ja') {
    const formatted = new Intl.DateTimeFormat(lang, {
      timeZone: getIanaTimeZone(timezone),
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(dateStr))
    return `${formatted} ${timezone}`
  }

  const formatted = new Intl.DateTimeFormat(lang, {
    timeZone: getIanaTimeZone(timezone),
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(dateStr))
  return `${formatted} ${timezone}`
}

/**
 * Formats size unit and gender for display without any casing transforms.
 * Returns base format like "US Men's" or "JP Women's" or just "US"
 * Use CSS text-transform for uppercase as needed in the UI
 *
 * Examples:
 * - formatSizeLabel('US', 'men') => "US Men's"
 * - formatSizeLabel('JP', 'women') => "JP Women's"
 * - formatSizeLabel('US') => "US"
 */
export const formatSizeLabel = (sizeUnit?: string, gender?: string): string => {
  const unit = sizeUnit?.toUpperCase() || 'US'

  if (!gender) return unit

  const lowerGender = gender.toLowerCase()
  if (lowerGender === 'men') {
    return `${unit} Men's`
  }
  if (lowerGender === 'women') {
    return `${unit} Women's`
  }

  // Fallback for any other gender value
  const formatted = gender.charAt(0).toUpperCase() + gender.slice(1).toLowerCase()
  return `${unit} ${formatted}`
}
